import {
  faCheckSquare,
  faChevronLeft,
  faChevronRight,
  faGripVertical,
  faMinusCircle,
  faPlusCircle,
  faSearch,
  faSquare,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa.js';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions.js';
import { DepartmentContext } from '../../../../../DepartmentWrapper.js';
import { deleteData, fetchDataAgnostic, postDataAgnostic, putData } from '../../../../../Services/dataApi.js';

import style from './ScheduleWizard.style.js';

const makeClientId = () =>
  `room_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;

const SortableRoomCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={sortableStyle}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
};

const ScheduleWizard = ({
  isOpen,
  setShowScheduleWizard,
  scheduleData,
  evaluators,
  candidates,
  updateAllData,
  forcedContext,
  pk_InterviewDate,
  initialStep = 1,
}) => {
  const [wizardStep, setWizardStep] = useState(1);
  const [startTime, setStartTime] = useState('');
  const [standardDuration, setStandardDuration] = useState(0);
  const [standardPassingDuration, setStandardPassingDuration] = useState(0);
  const [startWithWelcomeMeeting, setStartWithWelcomeMeeting] = useState(false);
  const [welcomeMeetingDuration, setWelcomeMeetingDuration] = useState(0);
  const [welcomeMeetingPassingDuration, setWelcomeMeetingPassingDuration] = useState(0);
  const [roomCount, setRoomCount] = useState(0);
  const [timeSlotsToAdd, setTimeSlotsToAdd] = useState(1);
  const [evaluatorSearchString, setEvaluatorSearchString] = useState('');
  const [wizardIsWorking, setWizardIsWorking] = useState(false);
  const [roomColumns, setRoomColumns] = useState([]);
  const [deletedColumnIds, setDeletedColumnIds] = useState([]);
  const [stepOneHeight, setStepOneHeight] = useState(0);
  const [highlightedRoom, setHighlightedRoom] = useState(null);
  const [pendingScrollRoom, setPendingScrollRoom] = useState(null);

  const originalColumnsRef = useRef({});
  const stepOneContainerRef = useRef(null);
  const roomCardRefs = useRef({});

  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const deptContext = useContext(DepartmentContext);
  const dContext = forcedContext ? forcedContext : deptContext;
  const history = useHistory();

  const { department = {} } = dContext;
  const { VirtualMeetingMode } = department;
  const embeddedMeetingsEnabled = VirtualMeetingMode === 'Internal' && Boolean(department.enableEmbeddedMeetings);
  const enforce35MinuteLimit = VirtualMeetingMode === 'Internal' && !embeddedMeetingsEnabled;

  const pathExploded = history.location.pathname.split('/');
  const scheduleID = pathExploded[pathExploded.length - 1];

  const headers = scheduleData && scheduleData.headers ? scheduleData.headers : [];
  const body = scheduleData && scheduleData.body ? scheduleData.body : [];
  const isInitializationMode = headers.length <= 0 && body.length <= 1;
  const candidatesCount = candidates ? Object.values(candidates).length : 0;

  const activeEvaluators = useMemo(() => {
    return Object.values(evaluators || {})
      .filter((e) => e && !e.Deleted)
      .sort((a, b) => {
        const aString = `${a.UserLast || ''}, ${a.UserFirst || ''}`;
        const bString = `${b.UserLast || ''}, ${b.UserFirst || ''}`;
        return aString.localeCompare(bString);
      });
  }, [evaluators]);

  const filteredEvaluators = useMemo(() => {
    const search = evaluatorSearchString ? evaluatorSearchString.toLowerCase().trim() : '';
    if (!search) {
      return activeEvaluators;
    }
    return activeEvaluators.filter((e) => {
      const eName = `${e.UserLast || ''} ${e.UserFirst || ''}`.toLowerCase();
      return eName.includes(search);
    });
  }, [activeEvaluators, evaluatorSearchString]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    if (!isOpen || !scheduleData) {
      return;
    }

    const { metaData = {} } = scheduleData;
    const { DateOfInterview, StartTime, StandardDurationInMinutes, StandardPassingDurationInMinutes } = metaData;

    const newStartTime = DateOfInterview && StartTime ? dayjs(`${DateOfInterview} ${StartTime}`).format('HH:mm') : '';
    const initialHeaders = scheduleData.headers || [];

    const columnsFromHeaders = initialHeaders.map((h, index) => {
      const evaluatorIds =
        h && h.Evaluators && h.Evaluators.Evaluators
          ? h.Evaluators.Evaluators.map((e) => e.pk_User).filter((id) => id != null)
          : [];
      return {
        clientId: `existing_${h.pk_ScheduleAssignment}`,
        pk_ScheduleAssignment: h.pk_ScheduleAssignment,
        roomLabel: h.RoomLabel || '',
        evaluatorIds,
        order: index,
      };
    });

    const baseCount = columnsFromHeaders.length;
    const mappedColumns = columnsFromHeaders.length > 0 ? columnsFromHeaders : [];

    const originalColumns = {};
    columnsFromHeaders.forEach((column) => {
      if (column.pk_ScheduleAssignment != null) {
        originalColumns[column.pk_ScheduleAssignment] = {
          evaluatorIds: [...column.evaluatorIds],
          roomLabel: column.roomLabel || '',
        };
      }
    });

    originalColumnsRef.current = originalColumns;

    setWizardStep(initialStep || 1);
    setStartTime(newStartTime);
    setStandardDuration(StandardDurationInMinutes || 0);
    setStandardPassingDuration(StandardPassingDurationInMinutes || 0);

    const firstRow = getFirstScheduleRow(scheduleData);
    const firstRowTitle = firstRow && firstRow.CustomMeetingTitle ? firstRow.CustomMeetingTitle.toLowerCase().trim() : '';
    const rowIsWelcomeMeeting = firstRowTitle === 'welcome meeting';
    const initialWelcomeDuration =
      firstRow && firstRow.CustomDurationInMinutes != null ? Number(firstRow.CustomDurationInMinutes) : 20;
    const initialWelcomePassingDuration =
      firstRow && firstRow.CustomPassingDurationInMinutes != null ? Number(firstRow.CustomPassingDurationInMinutes) : 5;
    setStartWithWelcomeMeeting(rowIsWelcomeMeeting);
    setWelcomeMeetingDuration(initialWelcomeDuration >= 1 ? initialWelcomeDuration : 1);
    setWelcomeMeetingPassingDuration(initialWelcomePassingDuration >= 0 ? initialWelcomePassingDuration : 0);

    setRoomCount(baseCount);
    setRoomColumns(mappedColumns);
    setDeletedColumnIds([]);
    setEvaluatorSearchString('');
    setHighlightedRoom(null);
    setPendingScrollRoom(null);

    const minimumRows = Math.max(candidatesCount, baseCount) + 2;
    setTimeSlotsToAdd(minimumRows > 0 ? minimumRows : 1);
  }, [isOpen, scheduleData, candidatesCount, initialStep]);

  useEffect(() => {
    const minimumRows = Math.max(candidatesCount, roomColumns.length, roomCount, 1) + 2;
    setTimeSlotsToAdd((prev) => {
      if (!prev || prev < minimumRows) {
        return minimumRows;
      }
      return prev;
    });
  }, [candidatesCount, roomColumns.length, roomCount]);

  useEffect(() => {
    if (!pendingScrollRoom || !pendingScrollRoom.clientId) {
      return;
    }

    const targetColumnExists = roomColumns.some((column) => column.clientId === pendingScrollRoom.clientId);
    if (!targetColumnExists) {
      return;
    }

    const targetNode = roomCardRefs.current[pendingScrollRoom.clientId];
    if (targetNode) {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    }
    setHighlightedRoom(pendingScrollRoom);
    setPendingScrollRoom(null);
  }, [pendingScrollRoom, roomColumns]);

  useEffect(() => {
    if (!highlightedRoom || !highlightedRoom.clientId) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setHighlightedRoom(null);
    }, 1800);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [highlightedRoom]);

  useEffect(() => {
    if (!isOpen || wizardStep !== 1 || !stepOneContainerRef.current) {
      return;
    }

    const measuredHeight = Math.ceil(stepOneContainerRef.current.getBoundingClientRect().height);
    if (measuredHeight > 0) {
      setStepOneHeight((prev) => (measuredHeight > prev ? measuredHeight : prev));
    }
  }, [
    isOpen,
    wizardStep,
    standardDuration,
    standardPassingDuration,
    startWithWelcomeMeeting,
    welcomeMeetingDuration,
    welcomeMeetingPassingDuration,
    roomCount,
    roomColumns.length,
    candidatesCount,
  ]);

  const calculateEndTime = () => {
    const { metaData = {} } = scheduleData || {};
    const { DateOfInterview } = metaData;

    if (!DateOfInterview || !startTime) {
      return '--';
    }

    const newTimeToEnd = dayjs(`${DateOfInterview} ${startTime}`).add(
      timeSlotsToAdd * (standardDuration + standardPassingDuration),
      'minutes',
    );

    return newTimeToEnd.format('hh:mm A');
  };

  const getSchedulePrimaryKey = () => {
    return pk_InterviewDate ? Number(pk_InterviewDate) : Number(scheduleID);
  };

  const getFirstScheduleRow = (sourceScheduleData = {}) => {
    const rows = Array.isArray(sourceScheduleData.body) ? sourceScheduleData.body : [];
    const rowsToSort = rows.filter((row) => row && row.pk_Timeslot !== 'END' && row.pk_Timeslot != null);
    if (rowsToSort.length <= 0) {
      return null;
    }
    return rowsToSort.sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))[0];
  };

  const getColumnIndexWithEvaluator = (pk_User) => {
    return roomColumns.findIndex((column) => {
      return column.evaluatorIds.includes(pk_User);
    });
  };

  const ensureRoomColumnCount = (targetCount) => {
    const safeCount = targetCount >= 0 ? targetCount : 0;
    setRoomColumns((prevColumns) => {
      let newColumns = [...prevColumns];
      if (safeCount > newColumns.length) {
        const toAdd = safeCount - newColumns.length;
        for (let i = 0; i < toAdd; i++) {
          newColumns.push({
            clientId: makeClientId(),
            pk_ScheduleAssignment: null,
            roomLabel: '',
            evaluatorIds: [],
            order: newColumns.length,
          });
        }
      } else if (safeCount < newColumns.length) {
        const toRemove = newColumns.slice(safeCount);
        const existingToDelete = toRemove
          .filter((column) => column.pk_ScheduleAssignment != null)
          .map((column) => column.pk_ScheduleAssignment);

        if (existingToDelete.length > 0) {
          setDeletedColumnIds((prevDeleted) => Array.from(new Set([...prevDeleted, ...existingToDelete])));
        }
        newColumns = newColumns.slice(0, safeCount);
      }

      return newColumns.map((column, index) => ({ ...column, order: index }));
    });
  };

  const addRoomColumn = () => {
    setRoomColumns((prevColumns) => {
      const newClientId = makeClientId();
      const newColumns = [
        ...prevColumns,
        {
          clientId: newClientId,
          pk_ScheduleAssignment: null,
          roomLabel: '',
          evaluatorIds: [],
          order: prevColumns.length,
        },
      ];
      setRoomCount(newColumns.length);
      setPendingScrollRoom({ clientId: newClientId, tone: 'success' });
      return newColumns;
    });
  };

  const removeRoomColumn = (columnIndex) => {
    setRoomColumns((prevColumns) => {
      if (prevColumns.length <= 0) {
        return prevColumns;
      }

      const columnToRemove = prevColumns[columnIndex];
      if (columnToRemove && columnToRemove.pk_ScheduleAssignment != null) {
        setDeletedColumnIds((prevDeleted) =>
          Array.from(new Set([...prevDeleted, columnToRemove.pk_ScheduleAssignment])),
        );
      }

      const newColumns = prevColumns
        .filter((_, index) => index !== columnIndex)
        .map((column, index) => {
          return { ...column, order: index };
        });
      setRoomCount(newColumns.length);
      return newColumns;
    });
  };

  const updateRoomLabel = (columnIndex, newLabel) => {
    setRoomColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      newColumns[columnIndex] = { ...newColumns[columnIndex], roomLabel: newLabel };
      return newColumns;
    });
  };

  const toggleEvaluatorOnColumn = async (columnIndex, evaluator) => {
    const evaluatorId = evaluator.pk_User;

    const targetColumn = roomColumns[columnIndex];
    if (!targetColumn) {
      return;
    }

    const alreadySelectedInTarget = targetColumn.evaluatorIds.includes(evaluatorId);

    if (alreadySelectedInTarget) {
      setRoomColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        const current = newColumns[columnIndex];
        newColumns[columnIndex] = {
          ...current,
          evaluatorIds: current.evaluatorIds.filter((id) => id !== evaluatorId),
        };
        return newColumns;
      });
      return;
    }

    const existingIndex = getColumnIndexWithEvaluator(evaluatorId);
    if (existingIndex >= 0 && existingIndex !== columnIndex) {
      const result = await Swal.fire({
        title: 'Move evaluator?',
        text: `Do you want to move ${evaluator.UserFirst || ''} ${evaluator.UserLast || ''} to this column?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Move',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) {
        return;
      }

      setRoomColumns((prevColumns) => {
        const newColumns = prevColumns.map((column, index) => {
          if (index === existingIndex) {
            return {
              ...column,
              evaluatorIds: column.evaluatorIds.filter((id) => id !== evaluatorId),
            };
          }

          if (index === columnIndex) {
            return {
              ...column,
              evaluatorIds: [...column.evaluatorIds, evaluatorId],
            };
          }

          return column;
        });

        return newColumns;
      });
      return;
    }

    setRoomColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      const current = newColumns[columnIndex];
      newColumns[columnIndex] = {
        ...current,
        evaluatorIds: [...current.evaluatorIds, evaluatorId],
      };
      return newColumns;
    });
  };

  const onRoomDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setRoomColumns((prevColumns) => {
      const oldIndex = prevColumns.findIndex((column) => column.clientId === active.id);
      const newIndex = prevColumns.findIndex((column) => column.clientId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return prevColumns;
      }

      const movedColumns = arrayMove(prevColumns, oldIndex, newIndex).map((column, index) => ({
        ...column,
        order: index,
      }));

      setRoomCount(movedColumns.length);
      return movedColumns;
    });
  };

  const addStartTime = () => {
    return getTokenSilently()
      .then((token) => {
        return putData(
          'department/interviewDate/putEntry',
          {
            pk_Department: dContext.department.pk_Department,
            pk_InterviewDate: pk_InterviewDate ? pk_InterviewDate : scheduleID,
            pk_Season: dContext.season.pk_Season,
          },
          {
            entry: {
              StartTime: startTime,
              StandardDurationInMinutes: standardDuration,
              StandardPassingDurationInMinutes: standardPassingDuration,
            },
          },
          formatBearerToken(token),
        );
      })
      .then((res) => res.data);
  };

  const fetchLatestScheduleData = () => {
    return getTokenSilently()
      .then((token) => {
        return fetchDataAgnostic(
          'department/season/interviewDate',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Season: dContext.season.pk_Season,
            pk_InterviewDate: getSchedulePrimaryKey(),
          },
          formatBearerToken(token),
        );
      })
      .then((res) => res.data);
  };

  const fetchLatestScheduleDataWithFirstRow = (attempt = 0) => {
    const maxAttempts = 3;
    return fetchLatestScheduleData().then((latestScheduleData) => {
      const firstRow = getFirstScheduleRow(latestScheduleData);
      if (firstRow || attempt >= maxAttempts - 1) {
        return latestScheduleData;
      }
      return new Promise((resolve) => {
        setTimeout(() => {
          fetchLatestScheduleDataWithFirstRow(attempt + 1).then(resolve);
        }, 250);
      });
    });
  };

  const addTimeSlots = () => {
    return getTokenSilently()
      .then((token) => {
        const rowSortOrders = [];
        for (let i = 0; i < timeSlotsToAdd; i++) {
          rowSortOrders.push(i);
        }

        return postDataAgnostic(
          'department/season/schedule/row',
          {
            pk_Department: dContext.department.pk_Department,
            pk_InterviewDate: pk_InterviewDate ? pk_InterviewDate : Number(scheduleID),
          },
          {
            rowSortOrders,
          },
          formatBearerToken(token),
        );
      })
      .then((res) => res.data);
  };

  const createColumns = (columnsToCreate) => {
    if (!columnsToCreate || columnsToCreate.length <= 0) {
      return Promise.resolve();
    }

    const payload = {
      payload: columnsToCreate.map((column) => {
        const item = {
          evaluatorPrimaryKeys: column.evaluatorIds || [],
          // sortOrder: column.order != null ? column.order : 0,
        };
        if (column.roomLabel && column.roomLabel.trim()) {
          item.RoomLabel = column.roomLabel.trim();
        }
        return item;
      }),
    };

    return getTokenSilently()
      .then((token) => {
        return postDataAgnostic(
          'department/season/schedule/column',
          {
            pk_Department: dContext.department.pk_Department,
            pk_InterviewDate: pk_InterviewDate ? pk_InterviewDate : parseInt(scheduleID),
          },
          payload,
          formatBearerToken(token),
        );
      })
      .then((res) => res.data);
  };

  const deleteColumns = (pk_ScheduleAssignments) => {
    if (!pk_ScheduleAssignments || pk_ScheduleAssignments.length <= 0) {
      return Promise.resolve();
    }

    return getTokenSilently().then((token) => {
      return deleteData(
        'department/season/schedule/column',
        {
          pk_Department: dContext.department.pk_Department,
          pk_InterviewDate: parseInt(scheduleID),
        },
        {
          pk_ScheduleAssignments,
          type: 'delete',
        },
        formatBearerToken(token),
      );
    });
  };

  const updateColumn = (column) => {
    const original = originalColumnsRef.current[column.pk_ScheduleAssignment] || { evaluatorIds: [], roomLabel: '' };
    const evaluatorIds = column.evaluatorIds || [];

    const writeEvaluatorPrimaryKeys = evaluatorIds.filter((id) => !original.evaluatorIds.includes(id));
    const deleteEvaluatorPrimaryKeys = original.evaluatorIds.filter((id) => !evaluatorIds.includes(id));
    const roomLabelChanged = (column.roomLabel || '') !== (original.roomLabel || '');

    if (writeEvaluatorPrimaryKeys.length <= 0 && deleteEvaluatorPrimaryKeys.length <= 0 && !roomLabelChanged) {
      return Promise.resolve();
    }

    const payload = {
      writeEvaluatorPrimaryKeys,
      deleteEvaluatorPrimaryKeys,
      TotalNumberOfSplits: column.rawTotalSplits != null ? column.rawTotalSplits : 1,
      // sortOrder: column.order != null ? column.order : 0,
    };

    if (column.roomLabel != null) {
      payload.RoomLabel = column.roomLabel;
    }

    return getTokenSilently().then((token) => {
      return putData(
        'department/season/schedule/evaluator',
        {
          pk_Department: dContext.department.pk_Department,
          pk_ScheduleAssignment: parseInt(column.pk_ScheduleAssignment),
          pk_InterviewDate: pk_InterviewDate || Number(scheduleID),
        },
        payload,
        formatBearerToken(token),
      );
    });
  };

  const syncColumns = () => {
    const newColumns = roomColumns.filter((column) => column.pk_ScheduleAssignment == null);
    const existingColumns = roomColumns.filter((column) => column.pk_ScheduleAssignment != null);

    const promises = [];

    if (newColumns.length > 0) {
      promises.push(createColumns(newColumns));
    }

    if (deletedColumnIds.length > 0) {
      promises.push(deleteColumns(deletedColumnIds));
    }

    existingColumns.forEach((column) => {
      promises.push(updateColumn(column));
    });

    return Promise.all(promises);
  };

  const saveWelcomeMeetingOnFirstRow = (sourceScheduleData) => {
    if (!startWithWelcomeMeeting) {
      return Promise.resolve();
    }

    const firstRow = getFirstScheduleRow(sourceScheduleData);
    if (!firstRow || firstRow.pk_Timeslot == null || firstRow.pk_Timeslot === 'END') {
      return Promise.resolve();
    }

    const timeslotPayload = {
      CustomMeetingTitle: 'Welcome Meeting',
      CustomDurationInMinutes: Number(welcomeMeetingDuration),
      CustomPassingDurationInMinutes: Number(welcomeMeetingPassingDuration),
      VirtualRoomType: firstRow.VirtualRoomType || 'AllDay',
      CustomMeetingDetails: firstRow.CustomMeetingDetails || '',
      CustomMeetingUrl: firstRow.CustomMeetingUrl || '',
      CustomRoomLabel: firstRow.CustomRoomLabel || '',
    };

    return getTokenSilently().then((token) => {
      return putData(
        'department/season/interviewDate/timeslot',
        {
          pk_Department: dContext.department.pk_Department,
          pk_Timeslot: firstRow.pk_Timeslot,
        },
        {
          timeslot: timeslotPayload,
        },
        formatBearerToken(token),
      );
    });
  };

  const save = () => {
    setWizardIsWorking(true);

    let saveFlow = Promise.resolve();
    if (isInitializationMode) {
      saveFlow = saveFlow
        .then(() => {
          return addStartTime();
        })
        .then(() => {
          return addTimeSlots();
        });
    }

    saveFlow
      .then(() => {
        return syncColumns();
      })
      .then(() => {
        if (!startWithWelcomeMeeting) {
          return Promise.resolve();
        }
        return fetchLatestScheduleDataWithFirstRow().then((latestScheduleData) => {
          return saveWelcomeMeetingOnFirstRow(latestScheduleData);
        });
      })
      .then(() => {
        toast.success(isInitializationMode ? 'Interview date initialized!' : 'Columns updated!');
        updateAllData();
        setWizardIsWorking(false);
        setShowScheduleWizard(false);
      })
      .catch((err) => {
        toast.error(isInitializationMode ? 'Interview date failed to initialize!' : 'Failed to update columns!');
        setWizardIsWorking(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const canSave = () => {
    if (wizardIsWorking) {
      return false;
    }

    if (isInitializationMode) {
      if (!startTime || standardDuration < 0 || standardPassingDuration < 0) {
        return false;
      }
      if (startWithWelcomeMeeting && (welcomeMeetingDuration < 1 || welcomeMeetingPassingDuration < 0)) {
        return false;
      }
      if (enforce35MinuteLimit && standardDuration + standardPassingDuration > 35) {
        return false;
      }
    }

    return true;
  };

  const getFirstInvalidRoomColumn = () => {
    return roomColumns.find((column) => !column.evaluatorIds || column.evaluatorIds.length <= 0) || null;
  };

  const focusRoomColumn = (clientId, tone = 'success') => {
    if (!clientId) {
      return;
    }
    setPendingScrollRoom({ clientId, tone });
  };

  const handleStepTwoSaveClick = () => {
    if (roomColumns.length <= 0) {
      toast.error('Please add at least one room');
      return;
    }

    const firstInvalidColumn = getFirstInvalidRoomColumn();
    if (firstInvalidColumn) {
      toast.error('Please select at least one evaluator per column');
      focusRoomColumn(firstInvalidColumn.clientId, 'error');
      return;
    }

    save();
  };

  const isEvaluatorDisabledForColumn = (evaluatorId, columnIndex) => {
    const existingIndex = getColumnIndexWithEvaluator(evaluatorId);
    return existingIndex >= 0 && existingIndex !== columnIndex;
  };

  const getSortedEvaluatorsForColumn = (columnIndex) => {
    const targetColumn = roomColumns[columnIndex];
    if (!targetColumn) {
      return filteredEvaluators;
    }

    const getAlphabeticalName = (evaluator) => {
      return `${evaluator.UserLast || ''}, ${evaluator.UserFirst || ''}`.toLowerCase();
    };

    return [...filteredEvaluators].sort((a, b) => {
      const aSelectedHere = targetColumn.evaluatorIds.includes(a.pk_User);
      const bSelectedHere = targetColumn.evaluatorIds.includes(b.pk_User);

      if (aSelectedHere !== bSelectedHere) {
        return aSelectedHere ? -1 : 1;
      }

      const aSelectedElsewhere = isEvaluatorDisabledForColumn(a.pk_User, columnIndex);
      const bSelectedElsewhere = isEvaluatorDisabledForColumn(b.pk_User, columnIndex);

      if (aSelectedElsewhere !== bSelectedElsewhere) {
        return aSelectedElsewhere ? 1 : -1;
      }

      return getAlphabeticalName(a).localeCompare(getAlphabeticalName(b));
    });
  };

  const renderEvaluatorItem = (columnIndex, evaluator) => {
    const isSelected = roomColumns[columnIndex].evaluatorIds.includes(evaluator.pk_User);
    const isDisabledElsewhere = isEvaluatorDisabledForColumn(evaluator.pk_User, columnIndex);

    let backgroundColor = '#ffffff';
    if (isSelected) {
      backgroundColor = '#81cf7a';
    } else if (isDisabledElsewhere) {
      backgroundColor = '#e9eaec';
    }

    return (
      <div
        key={`${roomColumns[columnIndex].clientId}_${evaluator.pk_User}`}
        style={{
          ...style.spacedBetweenRow,
          ...style.userItem,
          backgroundColor,
          opacity: isDisabledElsewhere && !isSelected ? 0.7 : 1,
          cursor: wizardIsWorking ? 'not-allowed' : 'pointer',
        }}
        onClick={() => {
          if (wizardIsWorking) {
            return;
          }
          toggleEvaluatorOnColumn(columnIndex, evaluator);
        }}
      >
        <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} />
        <div style={style.label}>
          {evaluator.UserLast || ''}
          {evaluator.UserFirst ? `, ${evaluator.UserFirst}` : ''}
        </div>
      </div>
    );
  };

  const renderStepOne = () => {
    return (
      <div ref={stepOneContainerRef} style={style.simpleRow}>
        <div
          style={{
            ...style.simpleColumn,
            width: '33.33%',
            minHeight: 435,
            padding: 10,
            borderRight: '1px solid gray',
          }}
        >
          <FormGroup>
            <Label style={style.inputLabel} for="startTime">
              Start Time
            </Label>
            <Input
              bsSize="sm"
              type="time"
              disabled={wizardIsWorking}
              name="startTime"
              id="startTime"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
              }}
              placeholder="Enter Time. . ."
            />
          </FormGroup>
          <FormGroup>
            <Label style={style.inputLabel}>Will you start with a welcome meeting?</Label>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 20, alignItems: 'center' }}>
              <FormGroup check style={{ marginBottom: 0 }}>
                <Label check style={{ cursor: wizardIsWorking ? 'not-allowed' : 'pointer' }}>
                  <Input
                    type="radio"
                    name="startWithWelcomeMeeting"
                    disabled={wizardIsWorking}
                    checked={startWithWelcomeMeeting}
                    onChange={() => {
                      setStartWithWelcomeMeeting(true);
                    }}
                  />{' '}
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check style={{ marginBottom: 0 }}>
                <Label check style={{ cursor: wizardIsWorking ? 'not-allowed' : 'pointer' }}>
                  <Input
                    type="radio"
                    name="startWithWelcomeMeeting"
                    disabled={wizardIsWorking}
                    checked={!startWithWelcomeMeeting}
                    onChange={() => {
                      setStartWithWelcomeMeeting(false);
                    }}
                  />{' '}
                  No
                </Label>
              </FormGroup>
            </div>
          </FormGroup>
          <FormGroup>
            <Label style={style.inputLabel} for="welcomeMeetingDuration">
              Welcome Meeting Duration
            </Label>
            <InputGroup size="sm">
              <Input
                bsSize="sm"
                type="number"
                disabled={wizardIsWorking || !startWithWelcomeMeeting}
                style={style.inputFields}
                name="welcomeMeetingDuration"
                id="welcomeMeetingDuration"
                value={welcomeMeetingDuration}
                placeholder="Enter Duration. . ."
                min={1}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  const newValue = Number.isNaN(parsed) ? 1 : Math.max(parsed, 1);
                  setWelcomeMeetingDuration(newValue);
                }}
              />

              <InputGroupText>minutes</InputGroupText>
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label style={style.inputLabel} for="welcomeMeetingPassingPeriod">
              Welcome Meeting Passing Period Duration
            </Label>
            <InputGroup size="sm">
              <Input
                bsSize="sm"
                disabled={wizardIsWorking || !startWithWelcomeMeeting}
                type="number"
                style={style.inputFields}
                name="welcomeMeetingPassingPeriod"
                id="welcomeMeetingPassingPeriod"
                value={welcomeMeetingPassingDuration}
                placeholder="Enter Passing Period. . ."
                min={0}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  const newValue = Number.isNaN(parsed) ? 0 : Math.max(parsed, 0);
                  setWelcomeMeetingPassingDuration(newValue);
                }}
              />

              <InputGroupText>minutes</InputGroupText>
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label style={style.inputLabel} for="interviewDuration">
              Interview Duration
            </Label>
            <InputGroup size="sm">
              <Input
                bsSize="sm"
                type="number"
                disabled={wizardIsWorking}
                style={style.inputFields}
                name="interviewDuration"
                id="interviewDuration"
                value={standardDuration}
                placeholder="Enter Duration. . ."
                onChange={(e) => {
                  const newValue = parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0;
                  setStandardDuration(newValue);
                }}
              />

              <InputGroupText>minutes</InputGroupText>
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <Label style={style.inputLabel} for="passingPeriod">
              Passing Period
            </Label>
            <InputGroup size="sm">
              <Input
                bsSize="sm"
                disabled={wizardIsWorking}
                type="number"
                style={style.inputFields}
                name="passingPeriod"
                id="passingPeriod"
                value={standardPassingDuration}
                placeholder="Enter Passing Period. . ."
                onChange={(e) => {
                  const newValue = parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0;
                  setStandardPassingDuration(newValue);
                }}
              />

              <InputGroupText>minutes</InputGroupText>
            </InputGroup>
          </FormGroup>
          <div>
            <div style={style.inputLabel}>
              <b> Block Duration: {`${standardDuration + standardPassingDuration} minutes`}</b>
            </div>
            {enforce35MinuteLimit && standardDuration + standardPassingDuration > 35 && (
              <div style={{ color: 'red', fontSize: '0.875rem', marginTop: 4 }}>Must not be more than 35mins</div>
            )}
          </div>
        </div>

        <div
          style={{
            ...style.simpleColumn,
            width: '33.33%',
            minHeight: 400,
            padding: 10,
            borderRight: '1px solid gray',
          }}
        >
          <Label style={style.inputLabel}>Evaluation Rooms</Label>
          <InputGroup size="sm">
            <Button
              size="sm"
              disabled={wizardIsWorking}
              onClick={() => {
                const newCount = roomCount - 1 >= 0 ? roomCount - 1 : 0;
                setRoomCount(newCount);
                ensureRoomColumnCount(newCount);
              }}
            >
              <FontAwesomeIcon icon={faMinusCircle} />
            </Button>

            <Input
              bsSize="sm"
              type="number"
              min={0}
              value={roomCount}
              style={{ textAlign: 'center' }}
              onChange={(e) => {
                const parsed = parseInt(e.target.value);
                const newCount = parsed >= 0 ? parsed : 0;
                setRoomCount(newCount);
                ensureRoomColumnCount(newCount);
              }}
            />

            <Button
              size="sm"
              disabled={wizardIsWorking}
              onClick={() => {
                const newCount = roomCount + 1;
                setRoomCount(newCount);
                ensureRoomColumnCount(newCount);
              }}
            >
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
          </InputGroup>

          <div style={{ ...style.largeBoldLabel, marginTop: 20 }}>{`${candidatesCount} Candidates`}</div>
          <div style={{ ...style.largeBoldLabel, marginTop: 10 }}>Estimated End Time: {calculateEndTime()}</div>
        </div>

        <div style={{ ...style.simpleColumn, width: '33.33%', minHeight: 400, padding: 10 }}>
          <div style={{ ...style.largeBoldLabel, marginTop: 20 }}>{`${roomColumns.length} Interview Rooms`}</div>
          <div style={style.largeBoldLabel}>Ready to configure</div>
          <div style={{ marginTop: 12 }}>
            <div style={style.label}>Next step will let you assign evaluators per room and set room labels.</div>
          </div>
        </div>
      </div>
    );
  };

  const renderColumnCard = (column, columnIndex, dragHandleProps = {}) => {
    const isHighlighted = highlightedRoom && highlightedRoom.clientId === column.clientId;
    const highlightTone = isHighlighted ? highlightedRoom.tone : '';
    const highlightedBorder = highlightTone === 'error' ? '2px solid #dc3545' : '2px solid #34a853';
    const highlightedBoxShadow =
      highlightTone === 'error' ? '0 0 0 3px rgba(220, 53, 69, 0.2)' : '0 0 0 3px rgba(52, 168, 83, 0.2)';

    return (
      <div
        key={column.clientId}
        ref={(node) => {
          if (node) {
            roomCardRefs.current[column.clientId] = node;
          } else {
            delete roomCardRefs.current[column.clientId];
          }
        }}
        style={{
          ...style.roomCard,
          border: isHighlighted ? highlightedBorder : style.roomCard.border,
          boxShadow: isHighlighted ? highlightedBoxShadow : 'none',
        }}
      >
        <div style={style.roomCardHeader}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <div
              style={{ display: 'none' }}
              aria-hidden="true"
              {...dragHandleProps.attributes}
              {...dragHandleProps.listeners}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </div>
            <div style={style.roomCardTitle}>{`Room ${columnIndex + 1}`}</div>
          </div>
          <Button size="sm" color="danger" disabled={wizardIsWorking} onClick={() => removeRoomColumn(columnIndex)}>
            <FontAwesomeIcon icon={faMinusCircle} />
          </Button>
        </div>

        <FormGroup style={{ marginBottom: 8 }}>
          <Label style={style.inputLabel}>Room Label</Label>
          <Input
            bsSize="sm"
            type="text"
            value={column.roomLabel}
            disabled={wizardIsWorking}
            placeholder="Enter room label"
            onChange={(e) => {
              updateRoomLabel(columnIndex, e.target.value);
            }}
          />
        </FormGroup>

        <div style={style.roomEvaluatorList}>
          {getSortedEvaluatorsForColumn(columnIndex).map((evaluator) => renderEvaluatorItem(columnIndex, evaluator))}
        </div>
      </div>
    );
  };

  const renderStepTwo = () => {
    return (
      <div style={{ ...style.simpleColumn, minHeight: stepOneHeight || undefined }}>
        <div style={{ ...style.simpleRow, marginBottom: 10, gap: 8 }}>
          <div style={{ flex: 1 }}>
            <InputGroup size="sm">
              <Input
                bsSize="sm"
                disabled={wizardIsWorking}
                type="text"
                placeholder="Search evaluators"
                value={evaluatorSearchString}
                onChange={(e) => {
                  setEvaluatorSearchString(e.target.value);
                }}
              />
              <InputGroupText>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroupText>
            </InputGroup>
          </div>
          <Button
            size="sm"
            color="primary"
            style={style.addRoomButton}
            disabled={wizardIsWorking}
            onClick={addRoomColumn}
          >
            <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: 6 }} /> Add Room
          </Button>
        </div>

        <div style={style.roomsContainer}>
          {roomColumns.length <= 0 ? (
            <div style={{ width: '100%', textAlign: 'center', color: '#4b4f56', fontWeight: 600, padding: '20px 0' }}>
              No rooms added yet. Click Add Room to begin.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRoomDragEnd}>
              <SortableContext
                items={roomColumns.map((column) => column.clientId)}
                strategy={horizontalListSortingStrategy}
              >
                {roomColumns.map((column, columnIndex) => {
                  return (
                    <SortableRoomCard key={column.clientId} id={column.clientId}>
                      {(dragHandleProps) => renderColumnCard(column, columnIndex, dragHandleProps)}
                    </SortableRoomCard>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      centered={true}
      style={{ maxWidth: '90%' }}
      onClosed={() => {
        setEvaluatorSearchString('');
      }}
    >
      <ModalHeader
        toggle={() => {
          setShowScheduleWizard(false);
        }}
      >
        Schedule Wizard - {wizardStep === 1 ? 'Step 1: Setup' : 'Step 2: Manage Columns'}
      </ModalHeader>
      <ModalBody>{wizardStep === 1 ? renderStepOne() : renderStepTwo()}</ModalBody>
      <ModalFooter>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
          <div>
            {wizardStep === 2 ? (
              <Button
                size="sm"
                color="secondary"
                disabled={wizardIsWorking}
                onClick={() => {
                  setWizardStep(1);
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: 6 }} />
                Back
              </Button>
            ) : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
            {wizardStep === 1 ? (
              <Button
                size="sm"
                color="primary"
                disabled={
                  wizardIsWorking || (enforce35MinuteLimit && standardDuration + standardPassingDuration > 35)
                }
                onClick={() => {
                  setWizardStep(2);
                }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 6 }} />
              </Button>
            ) : null}

            <Button
              size="sm"
              disabled={wizardIsWorking}
              color="danger"
              onClick={() => {
                setShowScheduleWizard(false);
              }}
            >
              Cancel
            </Button>

            {wizardStep === 2 ? (
              <Button
                size="sm"
                color="success"
                disabled={!canSave()}
                onClick={() => {
                  handleStepTwoSaveClick();
                }}
              >
                Save
              </Button>
            ) : null}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ScheduleWizard;
